import { Form } from "taltech-styleguide";
import { useNavigation } from "../../hooks";
import { RoleType } from "@dirootie-domain/iam";
import { useUserContext } from "../../_runtime/iam/session.context";
import { roleMapperText } from "../../_runtime/iam/session.provider";
import { IconRT } from "../icons/Icon";
import { OverlayTooltip } from "../../components-domain/generation-domain/specifications/OverlayTooltip";
import Tooltip from "react-bootstrap/Tooltip";

/**
 * Utility Function: getColor
 * 
 * This function returns a color based on whether the `kind` matches the `current` role.
 * 
 * @param {string} kind - The role type to check.
 * @param {string} current - The currently selected role.
 * @returns {string} - Returns "#e4067e" if the `kind` matches the `current` role, otherwise returns "black".
 */
const getColor = (kind: string, current: string) => {
  const color = kind === current ? "#e4067e" : "black";
  return color;
};

/**
 * Functional Component: RoleIconSwitcher
 * 
 * This component displays a role icon with a button that allows switching between roles.
 * The icon's color changes based on the currently selected role.
 * 
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {RoleType} props.targetRole - The role that the button represents.
 * @param {RoleType} props.currentRole - The current role of the user.
 * @param {function} props.onChange - A callback function that is triggered when the role is changed.
 * 
 * @returns {JSX.Element} A button containing an icon that changes based on the role selected.
 */
export const RoleIconSwitcher = ({targetRole, currentRole, onChange}:{targetRole: RoleType, currentRole: RoleType, onChange:(role:RoleType)=>void}) => {
  return <button className="edb-icon-sidebar">
    <IconRT
      kind={targetRole.toLocaleLowerCase()}
      color={getColor(targetRole, currentRole)}
      onClick={() => onChange(targetRole)}
    />
  </button>
}

/**
 * Functional Component: RoleSwitcher
 * 
 * This component provides a switcher for roles (Study Director, Lecturer, Student).
 * It allows the user to view the application from different role perspectives, and
 * updates the session accordingly. It is hidden for students.
 * 
 * @returns {JSX.Element | null} A form group containing role switch icons, or null if the user is a student.
 */
export const RoleSwitcher = () => {
  const session = useUserContext();
  const navigator = useNavigation();
  if (
    session.role === undefined ||
    session.userRole === "STUDENT"
  )
  {
    return <></>;
  }
  const currentRole = session.role!;

  const onChange = (role: RoleType) => {
    const routes = {
      STUDY_DIRECTOR: "/management",
      LECTURER: "/lecturers",
      STUDENT: "/students",
    };
    session.setImpersonatedRole(role);
    navigator.navigateToPath(routes[role]);
  };
  const roles: RoleType[] = ["STUDY_DIRECTOR", "LECTURER", "STUDENT"];
  return (
    <Form.Group controlId=":r1e:">
      <Form.Label></Form.Label>
      <div className="d-flex flex-column gap-2">
      {
        roles.map((role) => (
          <OverlayTooltip
          key={role}
          component={() => (
            <Tooltip className="tooltip-button" style={{ position: "fixed" }}>
              View as {roleMapperText[role]}
            </Tooltip>
          )}
        >
          <RoleIconSwitcher
            targetRole={role!}
            currentRole={currentRole}
            onChange={onChange}
          /></OverlayTooltip>
        ))
      }
      </div>
    </Form.Group>
  );
};
